import { Channel, Client, Member, Message } from "revolt.js";

type Role = {
    id: string;
} & Member["orderedRoles"][number][1];

export function getRolesInChannel(channel: Channel): Role[] {
    const server_id = channel.server_id;
    if (!server_id) return [];

    const server = channel.client.servers.get(server_id);
    if (!server) return [];

    /* Mentionable roles are the SERVER's roles, not just the roles
     * currently held by visible members: a role can be mentioned (and
     * rendered) even when no member holds it, and the server's map keeps
     * names live across role edits without waiting for member resyncs.
     * Sorted by rank like Server#orderedRoles for stable UI ordering. */
    return Object.keys(server.roles ?? {})
        .map((id) => ({ id, ...server.roles![id] }))
        .sort((a, b) => (a.rank ?? 0) - (b.rank ?? 0)) as Role[];
}

export function getRolesInClient(client: Client): Role[] {
    const roles: Map<string, Role> = new Map();

    client.members.forEach((member) => {
        member.orderedRoles.forEach(([id, data]) => {
            roles.set(id, {
                id,
                ...data,
            });
        });
    });

    return [...roles.values()];
}

export function isMentionedByRole(message: Message): boolean {
    if (!message.channel?.server_id) {
        return false;
    }
    const client = message.client;

    const member = client.members.getKey({
        server: message.channel?.server_id,
        user: client.user!._id,
    });

    const userRoleSet = new Set(member?.roles ?? []);
    return (message.role_mentions ?? []).some((role) => userRoleSet.has(role));
}
